import { supabaseClient } from "@/lib/supabase";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "../../../utils/auth";
import type { LoginSchemaType } from "../types/loginSchema";

const login = async (formData: LoginSchemaType) => {
	const { data, error } = await supabaseClient.auth.signInWithPassword({
		email: formData.email,
		password: formData.password,
	});
	if (error) throw error;
	if (data) return data;
};

interface UsePasswordLoginMutationProps {
	shouldNavigate?: boolean;
}
const usePasswordLoginMutation = ({
	shouldNavigate = true,
}: UsePasswordLoginMutationProps) => {
	const auth = useAuth();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	return useMutation({
		mutationKey: ["login"],
		mutationFn: async (formData: LoginSchemaType) => {
			return login(formData);
		},
		onError: (error) => {
			console.error(error);
			toast.error(error.message);
		},
		onSuccess: (data) => {
			if (data?.user?.id) {
				toast.info("Successfully logged in");
				auth.login(data.user.id);
				// The onSuccess callback is passed as a separate parameter to the mutate function
				// not as part of the mutation object
			} else if (!data?.user) {
				toast.error("Email or password incorrect");
			} else {
				toast("Unknown login error");
			}
			if (shouldNavigate) {
				navigate({ to: "/authentication-complete" });
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ["user"] });
			queryClient.invalidateQueries({ queryKey: ["profile"] });
		},
	});
};

export default usePasswordLoginMutation;
